'use client';

import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { createFreeUser } from '@src/actions/user.actions';
import { Checkbox } from '@src/components/Checkbox/Checkbox';
import { ErrorModalBase } from '@src/components/ErrorModalBase/ErrorModalBase';
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink';
import { Recaptcha } from '@src/components/Recaptcha/Recaptcha';
import { Spinner } from '@src/components/Spinner/Spinner';
import { schema } from '@src/constants/UserRegistrationSchema';
import { useConsents } from '@src/hooks/useConsents';
import { useLocalizedRouter } from '@src/hooks/useLocalizedRouter';
import { useLocale } from '@src/i18n/LocaleProvider';
import { useTrackCreateAccount } from '@src/utils/analytics.util';
import { filterBuilderAttributes } from '@src/utils/builder.util';
import { log } from '@src/utils/log.util';
import { omitNullish } from '@src/utils/object.util';
import { useRecaptchaV3 } from '@src/utils/recaptcha.util';
import { cn } from '@src/utils/tailwind';
import { Button } from '../Button/Button';
import { PasswordField } from '../Password/PasswordField';
import { usePasswordRuleText } from '../Password/usePasswordRuleText';
import { TextField } from '../TextField/TextField';
import { defaultProps } from './UserRegistration.defaults';
import { instrumentAction } from "@src/utils/instrumentation";
export interface UserRegistrationProps {
  theme: 'light' | 'dark';
  buttonTestId: string;
  attributes: any;
  termsAndConditionsLink: string;
  privacyPolicyLink: string;
  submitButtonText: string;
  redirectUrl?: string;
}
interface RegistrationFormFields {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}
export const UserRegistration = ({
  theme,
  buttonTestId,
  attributes,
  termsAndConditionsLink,
  privacyPolicyLink,
  submitButtonText,
  redirectUrl
}: UserRegistrationProps = defaultProps) => {
  const isDarkMode = theme === 'dark';
  const trackCreateAccount = useTrackCreateAccount();
  const localizedRouter = useLocalizedRouter();
  const [isLoading, setLoading] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [tncChecked, setTncChecked] = useState(false);
  const [tncError, setTncError] = useState(false);
  const {
    t: ts
  } = useLocale('user-registration-form');
  const {
    t,
    transform,
    locale
  } = useLocale('user-registration-form-errors');
  const ruleText = usePasswordRuleText();
  const {
    t: tc
  } = useLocale('common');
  const {
    data: consentData,
    sendConsent
  } = useConsents({
    fetchConsents: true
  });
  const {
    getRecaptchaToken
  } = useRecaptchaV3();
  const [isRegistrationSuccess, setIsRegistrationSuccess] = useState(false);
  const [error, setError] = useState(false);
  const searchParams = useSearchParams();
  const utmSource = searchParams.get('utm_source');
  const utmMedium = searchParams.get('utm_medium');
  const utmCampaign = searchParams.get('utm_campaign');
  const corporateConsent = consentData?.consents.find(consent => consent.slug === 'corporate');
  useEffect(() => {
    if (corporateConsent) {
      setConsentChecked(corporateConsent.defaultCheckboxValue);
    }
  }, [corporateConsent]);
  const formMethods = useForm<RegistrationFormFields>({
    mode: 'onTouched',
    defaultValues: {},
    resolver: yupResolver(schema)
  });
  const {
    register,
    trigger,
    watch,
    formState: {
      errors
    }
  } = formMethods;
  const password = watch('password');
  const email = watch('email');
  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const onPasswordSubmit = async () => {
    if (!firstName || !lastName || !email || errors?.email?.message || !password || errors?.password?.message || !tncChecked) return;
    try {
      setLoading(true);
      if (!corporateConsent?.consentText || !corporateConsent.showCheckbox) {
        void sendConsent({
          email,
          slug: 'corporate',
          value: true
        });
      } else {
        void sendConsent({
          email,
          slug: 'corporate',
          value: consentChecked === corporateConsent?.checkIndicatesConsent
        });
      }
      await getRecaptchaToken('createUser');
      await instrumentAction("UserActions.createFreeUser", createFreeUser)(omitNullish({
        firstName,
        lastName,
        emailAddress: email,
        password,
        localePreference: locale.codeSnakeCase,
        termsAndConditionsAgree: tncChecked,
        utmSource,
        utmMedium,
        utmCampaign
      }));
      trackCreateAccount();
      if (redirectUrl) {
        localizedRouter.push(redirectUrl);
        return;
      }
      setIsRegistrationSuccess(true);
    } catch (err: any) {
      log.error(`[UserRegistration] Error when creating user - ${err?.message}`, err);
      setLoading(false);
      setError(true);
    }
  };
  const onClick = async () => {
    if (isLoading) return;
    await trigger(['firstName', 'lastName', 'email', 'password']);
    if (!tncChecked) {
      setTncError(true);
      return;
    }
    await onPasswordSubmit();
  };
  if (isRegistrationSuccess) {
    return <div className="h-screen w-full">
        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col items-center py-10">
            <CheckCircleIcon className={cn('size-18', isDarkMode ? 'text-bodi-accent-bright-500' : 'text-bodi-accent-deep-500')} />
            <span className={cn('font-lateral text-[1.5rem] font-bold md:text-[2.25rem]', {
            'text-bodi-white': isDarkMode
          })}>
              {ts('congrats')}
            </span>
            <span className={cn({
            'text-bodi-gray-butters': isDarkMode
          })}>
              {ts('accountCreated2')}
            </span>
            <LocalizedLink data-testid="logInBtn" href="/login" rel="noopener noreferrer" className="mt-8">
              <Button onClick={onClick} size="medium" className="h-11 bg-bodi-accent-bright-500 md:h-15" type="button">
                {ts('continueToLogin')}
              </Button>
            </LocalizedLink>
          </div>
        </div>
      </div>;
  }
  return <div {...filterBuilderAttributes(attributes)} className="w-full">
      <FormProvider {...formMethods}>
        <form onSubmit={async e => {
        e.preventDefault();
        await onClick();
      }} className="mx-auto mt-7 flex w-full max-w-lg flex-col items-center p-4">
          <div className="mb-4 flex w-full flex-col gap-4 md:flex-row">
            <TextField className="w-full" labelText={ts('firstName')} id="firstName" data-testid="firstName" {...register('firstName', {
            shouldUnregister: false
          })} isInvalid={!!errors.firstName?.message} captionText={t(errors.firstName?.message)} disabled={isLoading} containerClassName={cn('w-full md:w-1/2', {
            'opacity-20': isLoading
          })} isDarkMode={isDarkMode} />
            <TextField className="w-full" labelText={ts('lastName')} id="lastName" data-testid="lastName" {...register('lastName', {
            shouldUnregister: false
          })} isInvalid={!!errors.lastName?.message} captionText={t(errors.lastName?.message)} disabled={isLoading} containerClassName={cn('w-full md:w-1/2', {
            'opacity-20': isLoading
          })} isDarkMode={isDarkMode} />
          </div>
          <TextField className="w-full" labelText={ts('email')} id="email" data-testid="email" {...register('email', {
          shouldUnregister: false
        })} isInvalid={!!errors.email?.message} captionText={t(errors.email?.message)} disabled={isLoading} containerClassName={cn({
          'opacity-20': isLoading
        })} isDarkMode={isDarkMode} />
          <PasswordField className="w-full" disabled={isLoading} ruleText={ruleText} isDarkMode={isDarkMode} />

          {corporateConsent?.consentText && <div className={cn('mt-4 w-full', {
          'opacity-20': isLoading
        })}>
              <div className="w-full border-t border-bodi-gray-light" />
              <div className="py-4">
                {corporateConsent.showCheckbox ? <Checkbox checked={consentChecked} onChange={event => {
              setConsentChecked(event.target.checked);
            }} isDarkMode={isDarkMode} disabled={isLoading}>
                    <span className={cn('prose', isDarkMode ? 'text-bodi-gray-butters' : 'text-black')} dangerouslySetInnerHTML={{
                __html: corporateConsent?.consentText
              }} />
                  </Checkbox> : <span className={cn('prose text-body-sm', isDarkMode ? 'text-bodi-gray-butters' : 'text-black')} dangerouslySetInnerHTML={{
              __html: corporateConsent?.consentText
            }} />}
              </div>
              <div className="w-full border-t border-bodi-gray-light" />
            </div>}

          <div className={cn('flex justify-start py-6', {
          'opacity-20': isLoading
        })}>
            <Checkbox checked={tncChecked} required={true} onChange={event_0 => {
            setTncChecked(event_0.target.checked);
            if (event_0.target.checked) {
              setTncError(false);
            }
          }} isDarkMode={isDarkMode} disabled={isLoading} />
            <p className={cn('mx-0 px-0 text-body-sm tracking-[.029px]', isDarkMode ? 'text-bodi-gray-butters' : 'text-bodi-black-text')}>
              {transform(t('disclaimer'), {
              link1: text => <LocalizedLink href={termsAndConditionsLink} className="text-bodi-blue-info" target="_blank">
                    {text}
                  </LocalizedLink>,
              link2: text_0 => <LocalizedLink href={privacyPolicyLink} className="text-bodi-blue-info" target="_blank">
                    {text_0}
                  </LocalizedLink>
            })}
            </p>
          </div>
          {tncError && <div className="-mt-4 mb-4 w-full">
              <p className="text-body-xs text-bodi-red-error">{t('tncErrorMessage')}</p>
            </div>}

          <Button onClick={onClick} size="medium" className="h-11 w-full bg-bodi-accent-bright-500 md:h-15" type="submit" data-testid={buttonTestId} disabled={isLoading} isLoading={isLoading} loadingComponent={<Spinner size="medium" className="fill-bodi-black text-bodi-white" />}>
            {submitButtonText}
          </Button>
          <div className={cn('mt-2', isDarkMode ? 'text-bodi-gray-butters' : 'text-bodi-black-text')}>
            {ts('haveAnAccount')}{' '}
            <LocalizedLink data-testid="logInBtn" href="/login" rel="noopener noreferrer">
              <span className="font-bold underline">{ts('login')}</span>
            </LocalizedLink>
          </div>
        </form>
      </FormProvider>
      <Recaptcha />
      {error && <ErrorModalBase title={tc('error-retry-title')} message={tc('error-retry-message')} ctaText={tc('error-retry-cta')} ctaProps={{
      'data-testid': 'okayBtn'
    }} onClick={() => {
      setError(false);
    }} />}
    </div>;
};